<script lang="ts" setup>
    import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import { useStorage } from '@vueuse/core'
    import ModulView from '@/js/components/Modul/ModulView.vue'
    import { useModuleStore } from '@/js/stores/modul'
    import { useEditStateStore } from '@/js/stores/editState'
    import { useLayoutStore, useStudiengangStore } from '@/js/stores'
    import ModulCreateDialog from '@/js/components/ModulHandBuch/ModulCreateDialog.vue'
    import { toModulSlug } from '@/js/composables/useSemester'

    const props = defineProps({
        modulHandbuch: {
            type: Object,
            required: true
        },
        filterModulesForUser: {
            type: Object,
            required: false,
            default: () => ({})
        }
    })

    const route = useRoute()
    const router = useRouter()

    const moduleStore = useModuleStore()

    const isLoading = computed(() => moduleStore.isMethodLoading('fetchingModules'))

    async function loadModules() {
        await moduleStore.fetchModulesByModulhandbuchId(props.modulHandbuch.modulHandbuchId)
    }

    onMounted(loadModules)
    watch(() => props.modulHandbuch.modulHandbuchId, loadModules)

    const activeProps = computed(() => {
        return { modulHandbuch: props.modulHandbuch }
    })

    const componentsMap = {
        titelblatt: defineAsyncComponent(() => import('@/js/components/ModulHandBuch/TitelBlatt.vue')),
        pdfoutput: defineAsyncComponent(() => import('@/js/components/ModulHandBuch/ModulHandbuchPdfOutput.vue'))
    }

    const layoutStore = useLayoutStore()
    const studiengangStore = useStudiengangStore()

    const studiengangForModulCreate = computed(() =>
        studiengangStore.getStudiengangById(props.modulHandbuch.studiengangId)
    )
    const fachbereichIdForModulCreate = computed(
        () => studiengangForModulCreate.value?.fachbereich?.split('/').pop() ?? ''
    )

    const itemHeight = computed(() => {
        return `height: calc(100vh - ${layoutStore.appBarHeight}px - ${layoutStore.filterRowHeight}px - 44px)`
    })

    const modules = computed(() => {
        let modules = []
        for (const modulId of props.modulHandbuch.modulIds) {
            let module = moduleStore.getModuleById(modulId)
            if (!module) {
                continue
            }

            if (props.filterModulesForUser) {
                if (
                    !(
                        module.modulverantwortliche.includes(props.filterModulesForUser) ||
                        module.lehrende.includes(props.filterModulesForUser)
                    )
                ) {
                    continue
                }
            }
            modules.push(module)
        }

        return modules
    })

    /**
     * Aktive View bestimmt sich aus dem URL-Param :modulSlug.
     * Ist kein Slug gesetzt, wird 'titelblatt' angezeigt.
     * Fallback auf localStorage falls keine URL-Parameter vorhanden (z.B. /modulhandbuecher).
     */
    const localActiveComponent = useStorage('lernleitstand-active-component', 'titelblatt')
    const localActiveModuleId = useStorage('lernleitstand-active-module-id', '0')

    const hasUrlParams = computed(() => !!(route.params.kuerzel && route.params.semester))

    const activeComponent = computed(() => {
        if (hasUrlParams.value) {
            return route.params.modulSlug
                ? 'module'
                : localActiveComponent.value === 'pdfoutput'
                  ? 'pdfoutput'
                  : 'titelblatt'
        }
        return localActiveComponent.value
    })

    const activeModuleId = computed(() => {
        if (hasUrlParams.value && route.params.modulSlug) {
            const slug = String(route.params.modulSlug)
            return modules.value.find((m) => toModulSlug(m.modulname ?? '') === slug)?.modulId ?? null
        }
        return localActiveModuleId.value
    })

    const currentModule = computed(
        () => modules.value.find((module) => module.modulId === activeModuleId.value) ?? null
    )

    const editStore = useEditStateStore()
    const showStillEditingDialog = ref(false)
    const pendingModulId = ref<string | undefined | null>(null)
    const pendingActiveComponent = ref<string | null>(null)

    /**
     * Navigiert zur gegebenen View. Bei URL-Routing via router.push(),
     * sonst Fallback auf localStorage.
     */
    function navigateTo(componentName: string, modulId?: string): void {
        if (hasUrlParams.value) {
            const kuerzel = String(route.params.kuerzel)
            const semester = String(route.params.semester)

            if (componentName === 'module' && modulId) {
                const module = modules.value.find((m) => m.modulId === modulId)
                if (module) {
                    const slug = toModulSlug(module.modulname ?? '')
                    router.push({
                        name: 'ModulhandbuchModul',
                        params: { kuerzel, semester, modulSlug: slug }
                    })
                    return
                }
            }
            // Titelblatt oder PDF → zurück zur MHB-URL ohne Modul-Slug
            // PDF-Zustand über localStorage weiterhin steuern
            localActiveComponent.value = componentName
            router.push({
                name: 'ModulhandbuchBySemester',
                params: { kuerzel, semester }
            })
        } else {
            // Fallback: kein URL-Routing, klassisches localStorage
            localActiveComponent.value = componentName
            if (modulId) {
                localActiveModuleId.value = modulId
            }
        }
    }

    function changeActiveComponent(componentName: string, modulId?: string): void {
        if (editStore.hasOpenEdits) {
            pendingModulId.value = modulId
            pendingActiveComponent.value = componentName
            showStillEditingDialog.value = true
        } else {
            navigateTo(componentName, modulId)
        }
    }

    function confirmNavigation() {
        editStore.resetAll()
        showStillEditingDialog.value = false
        navigateTo(pendingActiveComponent.value ?? 'titelblatt', pendingModulId.value ?? undefined)
        pendingActiveComponent.value = null
        pendingModulId.value = null
    }

    function cancelNavigation(newDialogValue?: boolean): void {
        if (!newDialogValue) {
            showStillEditingDialog.value = false
            pendingActiveComponent.value = null
            pendingModulId.value = null
        }
    }
</script>

<template>
    <div v-if="isLoading" class="d-flex justify-center mt-5 mb-5">
        <div class="d-flex flex-column">
            <v-progress-circular color="primary" size="75" indeterminate />
        </div>
    </div>

    <div v-else id="modulhandbuch-container" class="d-flex flex-row flex-nowrap w-100">
        <nav id="sidebar" class="sidebar d-flex flex-column justify-content-start w-40" :style="itemHeight">
            <div class="sidenav-link-container">
                <v-btn
                    block
                    density="comfortable"
                    class="font-weight-bold mb-2 text-transform-none"
                    :class="{ 'bg-primary-accent-2': 'titelblatt' === activeComponent }"
                    color="primary-accent-1"
                    :disabled="showStillEditingDialog"
                    @click="changeActiveComponent('titelblatt')"
                >
                    Titelblatt
                </v-btn>
                <v-btn
                    block
                    density="comfortable"
                    class="font-weight-bold mb-2 text-transform-none"
                    :class="{ 'bg-primary-accent-2': 'pdfoutput' === activeComponent }"
                    color="primary-accent-1"
                    :disabled="showStillEditingDialog"
                    @click="changeActiveComponent('pdfoutput')"
                >
                    Pdf Ansicht
                </v-btn>
                <div class="sidenav-module-container border border-primary rounded">
                    <v-btn
                        block
                        density="comfortable"
                        class="font-weight-bold mb-2 text-transform-none"
                        :class="{ 'bg-primary-accent-2': 'module' === activeComponent }"
                        color="primary-accent-1"
                        readonly
                    >
                        Module
                    </v-btn>
                    <ul class="sidenav-module-list">
                        <li>
                            <div class="sidenav-module-item sidenav-link my-3">
                                <ModulCreateDialog
                                    :modulhandbuch-id="modulHandbuch.modulHandbuchId"
                                    :studiengang-id="modulHandbuch.studiengangId"
                                    :fachbereich-id="fachbereichIdForModulCreate"
                                    :semester="modulHandbuch.semester"
                                    @modul-created="(modulId) => changeActiveComponent('module', modulId)"
                                />
                            </div>
                        </li>
                        <li
                            v-for="module in modules"
                            :key="module.modulId"
                            @click="!showStillEditingDialog && changeActiveComponent('module', module.modulId)"
                        >
                            <div
                                class="sidenav-module-item sidenav-link px-2"
                                role="button"
                                :class="{
                                    active: activeComponent === 'module' && activeModuleId === module.modulId
                                }"
                            >
                                {{ module.modulname }}
                            </div>
                            <v-divider class="my-2" />
                        </li>
                    </ul>
                </div>
            </div>
        </nav>
        <div id="modul-container" class="w-100 pa-3">
            <div v-if="activeComponent === 'module' && currentModule">
                <h2 class="font-weight-bold">
                    {{ currentModule?.modulname }}
                </h2>
                <ModulView :modul-id="currentModule.modulId" />
            </div>
            <div v-else>
                <component :is="componentsMap[activeComponent]" v-bind="activeProps" />
            </div>
        </div>
    </div>
    <v-dialog v-model="showStillEditingDialog" width="50%" @update:modelValue="cancelNavigation(false)">
        <v-card>
            <v-card-title>Ungespeicherte Änderungen</v-card-title>
            <v-card-text>
                Es liegen ungespeicherte Änderungen vor. Diese gehen beim Verlassen der aktuellen Modulansicht verloren.
            </v-card-text>
            <v-card-actions>
                <v-btn @click="cancelNavigation(false)">Abbrechen</v-btn>
                <v-btn color="red" @click="confirmNavigation">Trotzdem wechseln</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script></script>

<style scoped>
    .sidebar {
        border-right: 1px solid;
        border-right-color: black;
        z-index: 1000;
        position: sticky;
        top: 0;
        width: 20%;
    }

    .sidenav-link-container {
        padding: 1rem 1rem 1rem 0;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .sidenav-module-list {
        padding-inline-start: 10px;
        padding-inline-end: 10px;
    }

    .sidenav-module-item {
        padding: 0.2rem;
        border-radius: 0.2rem;
    }

    .sidenav-item {
        color: #ffffff;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
        display: block;
        line-height: 1;
        text-align: center;
        white-space: nowrap;
        vertical-align: baseline;
        border-radius: 0.25rem;
        transition:
            color 0.15s ease-in-out,
            background-color 0.15s ease-in-out,
            border-color 0.15s ease-in-out,
            box-shadow 0.15s ease-in-out;
    }

    .sidenav-link {
        cursor: pointer;
        text-decoration: none;
    }

    .sidenav-link:hover {
        color: white;
        background-color: #6760ab;
    }

    .sidenav-link.active {
        color: #ffffff;
        background-color: #6760ab;
    }

    li {
        list-style-type: none;
    }
</style>
